import type { ReactElement, ReactNode } from "react";
import { Children, cloneElement, Fragment, isValidElement, useState } from "react";

import type { IPlainTooltipProps } from "@similarweb/ui-components";
import { PlainTooltip } from "@similarweb/ui-components";

import useDeactivateOnOutsideClick from "app/pages/sales-intelligence/hooks/useDeactivateOnOutsideClick";

import { StyledContainer, StyledContent, StyledItemsWrap } from "./styles";

type ButtonProps = { onClick?(...args: unknown[]): void };

export type ExportVariantsDropdownProps<P extends ButtonProps> = {
    id?: string;
    button: ReactElement<P>;
    buttonTooltipProps?: Readonly<IPlainTooltipProps>;
    children: ReactNode;
    onClick?(): void;
    isCloseByClick?: boolean;
};

const CONTAINER_CLASSNAME = "export-variants-dd-container";

const ExportVariantsDropdown = <P extends ButtonProps>({
    button,
    buttonTooltipProps = { enabled: false },
    children,
    id,
    isCloseByClick = true,
}: ExportVariantsDropdownProps<P>) => {
    const [isOpened, setIsOpened] = useState(false);

    const containerUniqClassName = `${CONTAINER_CLASSNAME}.${id}`;

    function closeDropdown() {
        setIsOpened(false);
    }

    useDeactivateOnOutsideClick(containerUniqClassName, isOpened, closeDropdown);

    return (
        <StyledContainer
            className={`${CONTAINER_CLASSNAME} ${containerUniqClassName}`}
            data-automation="export-variant-dropdown"
        >
            <div>
                <PlainTooltip {...buttonTooltipProps}>
                    <div>
                        {cloneElement(button, {
                            ...button.props,
                            onClick(...args: unknown[]) {
                                setIsOpened(true);
                                button.props.onClick?.(...args);
                            },
                        })}
                    </div>
                </PlainTooltip>
            </div>
            <StyledContent open={isOpened}>
                <StyledItemsWrap>
                    {Children.map(children, (child) => {
                        if (!isValidElement(child) || child.type === Fragment) {
                            return child;
                        }

                        return cloneElement(child, {
                            // @ts-expect-error error TS2698: Spread types may only be created from object types.
                            ...child.props,
                            onClick(...args: unknown[]) {
                                if (isCloseByClick) {
                                    closeDropdown();
                                }

                                // @ts-expect-error error TS2339: Property 'onClick' does not exist on type 'unknown'.
                                child.props?.onClick(...args);
                            },
                        });
                    })}
                </StyledItemsWrap>
            </StyledContent>
        </StyledContainer>
    );
};

export default ExportVariantsDropdown;
